import React, { useState } from 'react';
import { Field, Form, Formik } from 'formik';
import {
  InputField,
  withFieldProps,
  SelectFieldWithLabel,
  BreadcrumbItem,
  withBreadcrumbsContext,
} from '@adjust/dashboard-components';
import { User } from '@adjust/dashboard-core/api/types';
import { keysToSnakeCase } from '@adjust/dashboard-core/lib/fp';
import { isEmail, isRequired, validate, ValidationMapper } from '@adjust/dashboard-core/lib/validations';
import { languages } from '@adjust/dashboard-core/lib/constants/languages';

import EditUserEmail from '@react/Pages/AccountSettingsPage/UserDetails/EditUserEmail/EditUserEmail';
import EditUserApiToken from '@react/Pages/AccountSettingsPage/UserDetails/EditUserApiToken/EditUserApiToken';
import { getScreenTitle } from '@react/Pages/AccountSettingsPage/UserDetails/UserDetails/utils';
import { UserDetailsMenu } from '@react/Pages/AccountSettingsPage/UserDetails/UserDetails/UserDetailsMenu';
import { UserDetailScreen, UserDetailsTimezone } from '@react/Pages/AccountSettingsPage/UserDetails/UserDetails/types';
import { EditUserPasswordContainer } from '@react/Pages/AccountSettingsPage/UserDetails/EditUserPassword/EditUserPassword.container';
import './UserDetails.scss';

const validation: ValidationMapper = {
  email: [
    [isRequired, () => t('field.email.error')],
    [isEmail, () => t('field.email.error')],
  ],
  firstName: [[isRequired, () => t('field.first-name.error')]],
  lastName: [[isRequired, () => t('field.last-name.error')]],
};

function getLanguages() {
  const mapLocales = item => ({ value: item.id, label: t(item.title) });

  return languages.map(mapLocales);
}

interface OwnProps {
  user: User;
  currentUser: User;
  isAccountOwner: boolean;
  timezones: UserDetailsTimezone[];
  isDisabled: boolean;
  closeSidebar: () => void;
  updateUser: (values: Record<string, unknown>) => void;
  showConfirm: () => void;
}

interface ContextProps {
  pushBreadcrumb: (breadcrumb: BreadcrumbItem) => void;
  removeBreadcrumb: (id: string) => void;
}

type Props = OwnProps & ContextProps;

const UserDetailsContentBase: React.FC<Props> = ({
  user,
  isAccountOwner,
  closeSidebar,
  removeBreadcrumb,
  pushBreadcrumb,
  timezones,
  updateUser,
  isDisabled,
  showConfirm,
}) => {
  const [screen, setScreen] = useState<UserDetailScreen>(UserDetailScreen.MAIN);

  const navigateTo = (nextScreen: UserDetailScreen) => {
    if (nextScreen === UserDetailScreen.MAIN) {
      removeBreadcrumb(UserDetailScreen.MAIN);
    } else {
      pushBreadcrumb({
        id: UserDetailScreen.MAIN,
        label: getScreenTitle(UserDetailScreen.MAIN),
        action: () => navigateTo(UserDetailScreen.MAIN),
      });
    }
    setScreen(nextScreen);
  };

  const onCancel = () => {
    navigateTo(UserDetailScreen.MAIN);
  };

  const onFormSave = values => {
    updateUser(keysToSnakeCase(values));
  };

  return (
    <div className="content-panel flex-one flex-box-column edit-user-details">
      <div className="content-title-box without-background">
        <h3 className="content-heading">{getScreenTitle(screen)}</h3>
      </div>

      <div className="sidebar-view flex-one flex-box-column">
        {screen === UserDetailScreen.MAIN && (
          <div className="content-inner flex-one flex-box-column">
            <Formik
              initialValues={{
                lastName: user.last_name,
                firstName: user.first_name,
                language: user.language,
                timezoneId: user.timezone_id,
              }}
              onSubmit={onFormSave}
            >
              {({ isValid, dirty }) => (
                <Form className="flex-one flex-box-column">
                  <div className="flex-one flex-box-column panel-content">
                    <div className="flex-auto panel-section">
                      <Field name="firstName" validate={validate(validation.firstName)}>
                        {withFieldProps(<InputField label={t('field.first-name')} type="text" />)}
                      </Field>

                      <Field name="lastName" validate={validate(validation.lastName)}>
                        {withFieldProps(<InputField label={t('field.last-name')} type="text" />)}
                      </Field>

                      <Field name="language">
                        {withFieldProps(
                          <SelectFieldWithLabel
                            options={getLanguages()}
                            placeholder={t('label.select-language')}
                            label={t('label.language')}
                          />,
                        )}
                      </Field>

                      <Field name="timezoneId">
                        {withFieldProps(
                          <SelectFieldWithLabel
                            options={[{ value: '', label: t('label.default') }, ...timezones]}
                            placeholder={t('field.select-timezone')}
                            label={t('field.time-zone')}
                          />,
                        )}
                      </Field>
                    </div>
                    <UserDetailsMenu user={user} isAccountOwner={isAccountOwner} navigateTo={navigateTo} />
                  </div>

                  <div className="panel-footer">
                    {!isAccountOwner && (
                      <button type="button" onClick={showConfirm} className="btn btn-destroy btn-block">
                        {t('action.delete-user')}
                      </button>
                    )}
                    <div className="flex-box-row">
                      <button type="button" onClick={closeSidebar} className="btn flex-one">
                        {t('action.cancel')}
                      </button>
                      <button
                        type="submit"
                        className="btn btn-main flex-one"
                        disabled={!isValid || !dirty || isDisabled}
                      >
                        {t('action.update')}
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}
        {screen !== UserDetailScreen.MAIN && (
          <div className="flex-one flex-box-column panel-content">
            {screen === UserDetailScreen.EMAIL && (
              <EditUserEmail
                userId={user.id}
                currentAccountId={user.current_account_id}
                userEmail={user.email}
                unconfirmedEmail={user.unconfirmed_email}
                isEmailOwner={isAccountOwner}
                onCancel={onCancel}
              />
            )}
            {screen === UserDetailScreen.PASSWORD && <EditUserPasswordContainer onCancel={onCancel} />}
            {screen === UserDetailScreen.API_TOKEN && <EditUserApiToken user={user} onCancel={onCancel} />}
          </div>
        )}
      </div>
    </div>
  );
};

const mapContextToProps = context => ({
  pushBreadcrumb: context.pushBreadcrumb,
  removeBreadcrumb: context.removeBreadcrumb,
});

export const UserDetailsContent =
  withBreadcrumbsContext<OwnProps, 'pushBreadcrumb' | 'removeBreadcrumb'>(mapContextToProps)(UserDetailsContentBase);
